import React from 'react';
import { Trophy, Users, TrendingUp, Activity } from 'lucide-react';
import { useLeaderboard } from '../hooks/useLeaderboardData';
import { StatsCard } from '../components/StatsCard';
import { RankBadge } from '../components/RankBadge';
import { TopBridgeUsers } from '../components/TopBridgeUsers';
import { formatCurrency, formatNumber } from '../utils/formatters';

const Home: React.FC = () => {
  const { 
    data: overallLeaderboard, 
    stats, 
    loading, 
    error 
  } = useLeaderboard({ filter: 'overall', limit: 5 });

  const top5 = overallLeaderboard.slice(0, 5);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Trophy className="w-8 h-8 text-yellow-400" />
            <h1 className="text-4xl md:text-5xl font-bold text-gradient-primary">
              Bridge Champions
            </h1>
          </div>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto">
            Top bridge users on the Shido Network
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Total Users"
            value={formatNumber(stats?.totalUsers || 0)}
            icon={<Users className="w-6 h-6" />}
            gradient="gradient-primary"
            loading={loading}
          />
          <StatsCard
            title="Total Volume"
            value={formatCurrency(stats?.totalVolume || '0')}
            icon={<TrendingUp className="w-6 h-6" />}
            gradient="gradient-secondary"
            loading={loading}
          />
          <StatsCard
            title="Total Transactions"
            value={formatNumber(stats?.totalTransactions || 0)}
            icon={<Activity className="w-6 h-6" />}
            gradient="gradient-accent"
            loading={loading}
          />
          <StatsCard
            title="Top User Volume"
            value={formatCurrency(stats?.topUser?.volume || '0')}
            icon={<Trophy className="w-6 h-6" />}
            gradient="gradient-success"
            loading={loading}
          />
        </div>

        {/* Top 3 Bridge Champions */}
        <TopBridgeUsers />

        {/* Top 5 Champions */}
        <div className="glass-card p-8 mb-8 animate-slide-up">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-3">
              <Trophy className="w-6 h-6 text-yellow-400" />
              Top 5 Champions
            </h2>
            <button
              onClick={() => window.location.hash = '/champions'}
              className="btn-secondary px-4 py-2 rounded-lg text-sm font-medium text-white hover:bg-white/20 transition-all"
            >
              View All →
            </button>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="loading-shimmer h-16 rounded-lg"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-400">
              Failed to load champions: {error}
            </div>
          ) : top5.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              No champions yet. Be the first to bridge!
            </div>
          ) : (
            <div className="space-y-4">
              {top5.map((user, index) => (
                <div
                  key={user.address}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <RankBadge rank={index + 1} />
                    <div>
                      <div 
                        className="font-mono text-white group-hover:text-blue-300 transition-colors cursor-pointer"
                        onClick={() => window.location.hash = `/user/${user.address}`}
                      >
                        {user.displayAddress}
                      </div>
                      <div className="text-sm text-slate-400">
                        {formatNumber(user.transactionCount)} transactions
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-white">
                      {formatCurrency(user.totalVolume)}
                    </div>
                    <div className="text-sm text-slate-400">
                      Total Volume
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            onClick={() => window.location.hash = '/champions'}
            className="glass-card p-6 text-left hover:bg-white/10 transition-all group"
          >
            <div className="flex items-center gap-4 mb-3">
              <Trophy className="w-8 h-8 text-yellow-400 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white">Full Leaderboard</h3>
            </div>
            <p className="text-slate-300">
              View complete rankings with filters and detailed stats
            </p>
          </button>

          <button
            onClick={() => window.location.hash = '/recent'}
            className="glass-card p-6 text-left hover:bg-white/10 transition-all group"
          >
            <div className="flex items-center gap-4 mb-3">
              <Activity className="w-8 h-8 text-green-400 group-hover:scale-110 transition-transform" />
              <h3 className="text-xl font-bold text-white">Recent Activity</h3>
            </div>
            <p className="text-slate-300">
              See the latest bridge transactions and user activity
            </p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;
